/**
 * Character-animation vocabulary — the CLOSED set of semantic animation state
 * names a creation may declare (per-entity `frames` maps in the render
 * manifest, sim-emitted `RenderEntity.state`) and the platform's default frame
 * count per state (GENERATION policy: how many cells a state's strip gets).
 *
 * Ownership split (character-animation plan, Key Decisions): the names are
 * owned by `@djgg/gameplay-kit` (composed modules declare the states they emit
 * against them) and re-exported here; frame counts live HERE
 * (`@djgg/creations` — validators, plan composer, callables, portal, prompt
 * interpolation); playback policy (fps, loop|oneShot) lives in
 * `@djgg/creation-shell` `src/animation.ts`, keyed by the SAME names. The
 * shell's contract test value-locks the two key sets; the client never derives
 * strip geometry, so frame counts must never appear shell-side.
 */

import { PlatformError } from '@djgg/core';
import { ANIM_STATE_NAMES, type AnimStateName } from '@djgg/gameplay-kit';
import { isValidAssetName, slugify } from './slug.js';

/** The closed vocabulary of semantic animation states — owned by
 *  `@djgg/gameplay-kit` (composed modules declare their states against it) and
 *  re-exported here for manifests, strips and the art plan. Any other name is
 *  rejected at the manifest boundary. */
export { ANIM_STATE_NAMES, type AnimStateName } from '@djgg/gameplay-kit';

/** Default strip frame count per state — generation policy (how many cells the
 *  asset pipeline draws for a state), NEVER read by the shell at runtime (the
 *  manifest's `strips` entries carry the authoritative per-strip geometry). */
export const ANIM_FRAME_DEFAULTS: Record<AnimStateName, number> = {
  idle: 2,
  move: 4,
  jump: 2,
  climb: 2,
  hang: 2,
  crouch: 2,
  dash: 2,
  shoot: 3,
  'action-1': 3,
  'action-2': 3,
  'action-3': 3,
  'action-4': 3,
  hit: 2,
  down: 2,
  win: 2,
};

const ANIM_STATE_SET: ReadonlySet<string> = new Set(ANIM_STATE_NAMES);

/** True when `v` is a member of the closed {@link ANIM_STATE_NAMES} vocabulary. */
export function isAnimStateName(v: string): v is AnimStateName {
  return ANIM_STATE_SET.has(v);
}

/**
 * The ONE derivation of a strip asset's name: `<slugified-entity>-<state>`.
 * Used by the plan composer, the studio callable, the asset-gen worker, the
 * binding injector, and the portal — the flat `name` column is the only
 * queryable fold-in key for a strip, so every writer MUST derive it here.
 *
 * The composed name must pass the existing asset-name slug gate; when it
 * can't (entity name reduces to nothing, or the composition overflows the
 * 40-char cap) this THROWS `PlatformError('validation')` — never a silently
 * reshaped name (a strip the injector can't re-derive is a dead strip).
 */
export function stripAssetName(entityName: string, state: AnimStateName): string {
  const entity = slugify(entityName);
  const name = `${entity}-${state}`;
  if (!entity || !isValidAssetName(name)) {
    throw new PlatformError(
      'validation',
      `cannot derive a strip asset name from entity ${JSON.stringify(entityName)} + state "${state}" ` +
        `(composed "${name}" must be a 2..40 char slug)`,
    );
  }
  return name;
}

/**
 * Write-time assertion that a strip asset's `name` IS the structural
 * `<entity>-<state>` key derived from its own metadata. Throws
 * `PlatformError('validation')` on an unknown state or a name mismatch —
 * the invariant every strip consumer (injector, portal, regen) relies on.
 */
export function assertStripAssetName(
  name: string,
  strip: { entityName: string; state: string },
): void {
  if (!isAnimStateName(strip.state)) {
    throw new PlatformError(
      'validation',
      `strip asset "${name}" declares unknown animation state "${strip.state}" ` +
        `(closed vocabulary: ${ANIM_STATE_NAMES.join(', ')})`,
    );
  }
  const expected = stripAssetName(strip.entityName, strip.state);
  if (name !== expected) {
    throw new PlatformError(
      'validation',
      `strip asset name "${name}" must be the derived "${expected}" ` +
        `(entity "${strip.entityName}" + state "${strip.state}")`,
    );
  }
}
